/* These mixins are designed to work with BankRec create/edit Views
 * Assumes parent component has all necessary data elements
 * e.g. Vuex store, ElementUI, etc.
*/
/* eslint-disable camelcase */
import moment from 'moment'
import { notify } from '@/utils/helpers/Notify'
import { validate } from '@/utils/helpers/Validate'
import { formatDateFields } from '@/utils/helpers/FormatDate'
import { ManualCheckLoader } from '@/components/Programs/BankRecon/ManualCheckLoader'

export const bankRecMixins = {
  data () {
    return {
      form: {
        bank: '',
        start: '',
        end: '',
        cash_account_balance: '',
        balance_decimal: '',
        previous_balance: '',
        show_all_checks: false,
        cleared_amount: '',
        difference: '',
        completed_time: false
      },
      initForm: {},
      selection: {
        ap: [],
        je: [],
        pc: [],
        checks: [],
        payments: []
      },
      datesAreValid: true,
      prevFormState: {},
      saveable: false,
      isSaving: false,
      showMarkAsCompleteDialog: true,
      tableHeight: 1000,
      currentPage: 1,
      reconcilablesCount: 0,
      loading: false,
      noData: false,
      activeTab: 'ap',
      ledgerModalVisible: false,
      tableReady: true,
      reconcilables: {
        ap: { data: [], page: [] },
        je: { data: [], page: [] },
        pc: { data: [], page: [] },
        checks: { data: [], page: [] },
        payments: { data: [], page: [] }
      },
      ledgerItem: {
        meta_name: '',
        type: '',
        amount: '',
        date: '',
        description: '',
        related: []
      },
      pageLimit: 1000,
      activeTabRows: '',
      busy: false,
      tabChanged: false,
      gridApi: undefined
    }
  },
  methods: {
    /**
     * Displays a dialog asking the user if they wish to mark this recon as complete
     */
    checkIfComplete () {
      if (this.showMarkAsCompleteDialog && !this.form.completed_time) {
        const text = 'The reconciled amount is equal to the bank statement amount. Do you wish to mark this reconciliation as complete?'
        this.$confirm(text, 'Mark as Complete?', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
              this.form.completed_time = true
            } else {
              this.showMarkAsCompleteDialog = false
            }
            done()
          }
        }).catch(e => console.log(e))
      }
    },
    /**
     * Handles actions when the focus changes from one form field to another
     * @param field   The name of the field being changed
     */
    fieldFocusChanged (field) {
      if (['start', 'end'].includes(field) && this.formatDateFields()) {
        if (field === 'end' && this.form.bank && this.formHasChanged) {
          this.loadData()
        }
      }
    },
    /**
     * Handles actions when the bank select dropdown value changes
     * @param field   The name of the field being changed ("bank" in this component)
     */
    selectMenuChanged (field) {
      if (field === 'bank' && this.form.bank && !!this.datesAreValid && this.formHasChanged) {
        this.loadData()
      }
    },
    /**
     * Handles actions when changes to the form are made
     * @param field   The field being changed
     */
    formChanged (field) {
      switch (field.field) {
        case 'show_all_checks':
          if (this.formHasChanged) {
            this.loadData().catch(e => console.log(e))
          }
          break
        case 'completed_time':
        case 'balance_decimal':
          this.calculateSelectedItems()
      }
    },
    /**
     * Formats start and end date fields to YYYY-MM-DD format
     */
    formatDateFields () {
      formatDateFields(this, 'start')
      formatDateFields(this, 'end')
      this.datesAreValid = !!(this.form.start && this.form.end)
      return this.datesAreValid
    },
    /**
     * Loads checks from the API and renders them in the table
     */
    async loadData () {
      this.activeTab = Object.keys(this.reconcilables)[0]
      this.changePage(1)
      const { form, $store } = this
      this.prevFormState = Object.assign({}, form)
      const { bank, end, show_all_checks } = form
      const query = show_all_checks ? { bank } : { end, bank }
      let promises = [
        $store.dispatch('fetchBankStatementsByBank', bank),
        $store.dispatch('fetchReconcilables', { type: 'lines/ap', query }),
        $store.dispatch('fetchReconcilables', { type: 'lines/je', query }),
        $store.dispatch('fetchReconcilables', { type: 'lines/pc', query }),
        $store.dispatch('fetchReconcilables', { type: 'checks', query }),
        $store.dispatch('fetchReconcilables', { type: 'payments', query })
      ]
      if (this.bankRec && this.bankRec.id) {
        promises = [
          ...promises,
          ...[
            $store.dispatch('fetchLinesWithQuery', { type: 'ap', query: { recon: this.bankRec.id } }),
            $store.dispatch('fetchLinesWithQuery', { type: 'je', query: { recon: this.bankRec.id } }),
            $store.dispatch('fetchLinesWithQuery', { type: 'pc', query: { recon: this.bankRec.id } }),
            $store.dispatch('fetchAllChecksWithQuery', { recon: this.bankRec.id }),
            $store.dispatch('fetchAllPaymentsWithQuery', { recon: this.bankRec.id })
          ]
        ]
      }
      this.resetDataState()
      this.loading = true
      try {
        let apiResponse = await Promise.all(promises)
        if (apiResponse && apiResponse.length) {
          const manualCheckLoader = new ManualCheckLoader(this.$store, { id: null, apiResponse })
          await manualCheckLoader.fetchManualChecks()
          this.initializeData(apiResponse)
          this.form.previous_balance = this.statements && this.statements.next.ending_balance ? this.statements.next.ending_balance : ''
        } else {
          this.noData = true
        }
        this.loading = false
        return apiResponse
      } catch (e) {
        console.log(e)
        this.loading = false
        return e
      }
    },
    /**
     * Initializes the data
     */
    initializeData (apiResponse) {
      apiResponse = this.formatApiResponse(apiResponse)
      this.setLineItemCount(apiResponse)
      this.formatLineItems(apiResponse)
      this.selectAttachedLineItems(apiResponse)
      this.activeTab = this.initializeActiveTab(apiResponse)
    },
    /**
     * Receives data response from the API and marks attached line items as selected
     * TODO: maybe add duplication check in first conditional
     */
    selectAttachedLineItems (apiResponse) {
      if (this.bankRec && this.bankRec.id && apiResponse) {
        const attached = apiResponse.filter(v => v.entities && (!v.recon && !v.hasOwnProperty('statements')))
          .reduce((a, b) => a.concat(b.entities), [])
        attached.forEach(v => {
          if (!v.type) return v
          this.selection[v.type].push(v)
        })
        setTimeout(() => this.restoreSelection())
      }
    },
    /**
     * normalizes the data by checking
     * for a 'lines' or 'checks' property in the 'entities' property, and if found, replaces 'entities' with a simple
     * array of all lines or checks. Also gets a total count of all reconcilables.
     * @param apiResponse
     */
    formatApiResponse (apiResponse) {
      const setType = (data) => {
        return data.map(item => {
          switch (item.meta_type) {
            case 'journalentryline':
              item.type = 'je'
              break
            case 'accountspayableline':
              item.type = 'ap'
              break
            case 'pettycashline':
              item.type = 'pc'
              break
            case 'papercheck':
              item.type = 'checks'
              break
            case 'payment':
              item.type = 'payments'
              break
          }
          return item
        })
      }
      return apiResponse.map(resp => {
        if (resp.entities && resp.entities.lines) {
          resp.entities = setType(Object.values(resp.entities.lines))
        } else if (resp.entities && resp.entities.checks) {
          resp['type'] = 'checks'
          resp.entities = setType(Object.values(resp.entities.checks))
        } else if (resp.entities && resp.entities.payments) {
          resp['type'] = 'payments'
          resp.entities = setType(Object.values(resp.entities.payments))
        } else if (typeof resp.entities === 'object' && Object.keys(resp.entities).length) {
          resp.entities = setType(Object.values(resp.entities))
        } else {
          resp.entities = []
        }
        return resp
      }).filter(resp => !resp.entities.banks)
    },
    /**
     * Counts the number of line items returned by the api, and assigns that value to the 'reconcilablesCount'
     * data property.
     * @param apiResponse
     */
    setLineItemCount (apiResponse) {
      this.reconcilablesCount = apiResponse.filter(v => !v.hasOwnProperty('statements'))
        .map(v => v.count)
        .reduce((a, b) => a + b)
    },
    /**
     * Receives data response from the API, sorts it, and puts it into this.reconcilables.
     * Note: sorting is different for APs, since the sort order is, if manual check, sort by payment number ascending, then
     * by ledger_id ascending.
     * @param apiResponse
     */
    formatLineItems (apiResponse) {
      const sort = (data, type = null) => {
        if (type !== 'ap') {
          return data.sort((a, b) => new Date(b.time_posted) - new Date(a.time_posted))
        }
        const { getApById } = this.$store.getters
        const isManualCheck = (line) => getApById(line.transaction_id) && getApById(line.transaction_id).payment_type === 'CHECK_MANUAL'
        for (let line of data) {
          if (line.type === 'ap' && !isManualCheck(line)) {
            let ap = getApById(line.transaction_id)
            line.number = ap.payment_confirmation_number
          }
        }
        let manualChecks = data.filter(line => isManualCheck(line))
        let notManualChecks = data.filter(line => !isManualCheck(line))
        if (manualChecks.length) {
          manualChecks = manualChecks.sort((a, b) => {
            const apA = getApById(a.transaction_id)
            const apB = getApById(b.transaction_id)
            let aNum, bNum
            if (apA.payment_confirmation_number || apB.payment_confirmation_number) {
              aNum = isNaN(parseInt(apA.payment_confirmation_number)) ? 0 : parseInt(apA.payment_confirmation_number)
              bNum = isNaN(parseInt(apB.payment_confirmation_number)) ? 0 : parseInt(apB.payment_confirmation_number)
            }
            return aNum - bNum || a.ledger_id - b.ledger_id
          })
        }
        if (notManualChecks.length) {
          notManualChecks = notManualChecks.sort((a, b) => a.ledger_id - b.ledger_id)
        }
        return [...manualChecks, ...notManualChecks]
      }
      apiResponse.forEach(resp => {
        switch (resp.type) {
          case 'ap':
          case 'lines/ap':
          case 'je':
          case 'lines/je':
          case 'pc':
          case 'lines/pc':
            if (resp.type.includes('ap')) {
              const { getApById } = this.$store.getters
              for (let entity of resp.entities) {
                let ap = getApById(entity.transaction_id)
                entity.payment_type = ap.payment_type
                if (!entity.number && ap.number) {
                  entity.number = ap.number
                }
              }
            }
            const type = resp.type.includes('/') ? resp.type.split('/')[1] : resp.type
            if (resp.entities) {
              this.reconcilables[type].data = sort([...this.reconcilables[type].data, ...resp.entities], type)
            } else {
              this.reconcilables[type].data.push(resp)
              this.reconcilables[type].data = sort(this.reconcilables[type].data, type)
            }
            break
          case 'checks':
            if (resp.entities) {
              this.reconcilables.checks.data = [...this.reconcilables.checks.data, ...resp.entities]
                .sort((a, b) => parseInt(a.number) - parseInt(b.number))
            } else {
              this.reconcilables.checks.data.push(resp)
              this.reconcilables.checks.data = this.reconcilables.checks.data.sort((a, b) => parseInt(a.number) - parseInt(b.number))
            }
            break
          case 'payments':
            if (resp.entities) {
              this.reconcilables.payments.data = [...this.reconcilables.payments.data, ...resp.entities]
                .sort((a, b) => parseInt(a.number) - parseInt(b.number))
            } else {
              this.reconcilables.payments.data.push(resp)
              this.reconcilables.payments.data = this.reconcilables.payments.data.sort((a, b) => parseInt(a.number) - parseInt(b.number))
            }
        }
      })
    },
    /**
     * Sets the currently active tab after data reload
     * @param resp
     * @return {string}
     */
    initializeActiveTab (resp) {
      const validTypes = Object.keys(this.reconcilables)
      for (let i = 0; i < resp.length; i++) {
        if (!resp[i].type) continue
        if (resp[i].count) {
          const type = resp[i].type.includes('/') ? resp[i].type.split('/')[1] : resp[i].type
          if (validTypes.includes(type)) {
            return type
          }
        }
      }
      return validTypes[0]
    },
    /**
     * Resets the reconcilable transactions object back to its empty state, resets the count
     * of all reconcilable transactions to 0, resets the selection object, and all amounts.
     */
    resetDataState () {
      this.reconcilables = Object.assign({}, {
        ap: { data: [], page: [] },
        je: { data: [], page: [] },
        pc: { data: [], page: [] },
        checks: { data: [], page: [] },
        payments: { data: [], page: [] }
      })
      this.reconcilablesCount = 0
      this.selection = Object.assign({}, {
        ap: [],
        je: [],
        pc: [],
        checks: [],
        payments: []
      })
      this.form.cleared_amount = '0.00'
      this.form.difference = '0.00'
    },
    handleGridUpdate (event) {
      this.gridApi = event.api
      this.restoreSelection()
    },
    /**
     * Handles transaction selection
     * @param selection
     * @param row
     */
    handleSelectionChange (event) {
      const { activeTab } = this

      let selectedNode = event.node
      let row = selectedNode.data
      // If the node is not a row, select/deselect all of its children
      if (!row && selectedNode.allChildrenCount > 1) {
        for (let rowNode of selectedNode.allLeafChildren) {
          let isSelected = selectedNode.isSelected()
          rowNode.setSelected(isSelected)
        }
      }

      let selectedNodes = event.api.getSelectedNodes()
      let selection = new Set()
      for (let node of selectedNodes) {
        if (node.data) {
          selection.add(node.data)
        }
      }

      if (this.bankRec && this.bankRec.id) {          // if the bankrec exists (i.e edit view), call update on row(s)
        this.handleUpdateOnSelection(Array.from(selection), row)
      }
      this.selection[activeTab] = [...selection]
      if (['checks', 'ap', 'je', 'pc', 'payments'].includes(activeTab)) {
        this.calculateSelectedItems()
      }
    },
    /**
     * Determines which update operations should be performed based on whether the selection was a single-row
     * selection or a 'select all' type of selection, where multiple rows are selected at once. If the selection
     * is a single row, row param will contain an object representing the reconcilable line/check selected. If
     * the selection is multiple, row will be undefined, and thus will default to null.
     * @param selection
     * @param row
     */
    handleUpdateOnSelection (selection = [], row = null) {
      if (row) {                                        // Only one row has been selected
        if (selection.find(reconcilable => reconcilable.id === row.id)) {
          if (!row.recon || (row.recon && row.recon !== this.bankRec.id)) {
            this.updateReconcilable(row)                  // ...the reconcilable has been checked - attach it to bankrec
          }
        } else {
          this.updateReconcilable(row, true)    // ...the reconcilable has been unchecked - detach it from bankrec
        }
      }
      // else {                                          // "Select all" checkbox was clicked - either all or none were selected
      //   if (selection.length) {                         // ...all rows were selected.
      //     const toAttach = selection.filter(reconcilable => !reconcilable.recon)  // Figure out which ones need to be updated
      //     this.batchUpdateReconcilables(toAttach)
      //   } else {                                        // ..all rows were de-selected
      //     const toDetach = this.paginatedData.filter(reconcilable => reconcilable.recon)
      //     this.batchUpdateReconcilables(toDetach)
      //   }
      // }
    },
    /**
     * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
     * due to a page or tab change
     */
    restoreSelection () {
      const type = this.activeTab
      if (this.gridApi) {
        for (let rowData of this.selection[type]) {
          let rowNode = this.gridApi.getRowNode(rowData.id)
          if (rowNode) {
            rowNode.setSelected(true)
          }
        }
        this.tabChanged = false
        if (['ap'].includes(type)) {
          this.activeTabRows = Object.values(Object.groupBy(this.reconcilables[type].data.filter(x => x.payment_type !== 'CHECK'), ({ number, payment_type, ledger_id, transaction_id }) => number ? [number, payment_type] : [ledger_id])).length
        } else if (['checks'].includes(type)) {
          this.activeTabRows = Object.values(Object.groupBy(this.reconcilables[type].data, ({ number }) => number)).length
        } else if (['payments'].includes(type)) {
          this.activeTabRows = Object.values(Object.groupBy(this.reconcilables[type].data, ({ number, payment_type }) => [number, payment_type])).length
        } else {
          this.activeTabRows = this.reconcilables[type].data.length
        }
      }
    },
    /**
     * Triggers navigation back to BankRecListView or this.altRoute, if set
     * @param reset
     */
    goBack (reset = false) {
      this.showMarkAsCompleteDialog = false
      let alt
      if (reset) {
        alt = this.altRoute
        this.form = { ...this.initForm }
      }
      if (alt) return this.$router.push({ name: alt })
      return this.$router.push({ name: 'BankRecs' })
    },
    /**
     * Creates a new bankrec
     * @return {Promise.<void>}
     */
    async createBankRec () {
      try {
        this.isSaving = true
        const { r, markAsComplete, err } = await this.create()
        if (err) {
          throw new Error(err)
        }
        let updated, completed
        if (r && r.id) {
          updated = await this.updateLineItems(r.id)
          if (markAsComplete) {
            completed = await this.markAsComplete(r.id)
          }
          if (!(completed && Object.keys(completed).length || updated && (Array.isArray(updated)))) {
            throw new Error()
          }
          notify(this, 'success', 'Bank Reconciliation saved')
          this.$router.push({ name: 'ViewBankRec', params: { id: r.id } })
        } else {
          throw new Error()
        }
      } catch (e) {
        console.log(e)
        if (e.message !== 'validation') {
          notify(this, 'error', e.message || 'Error saving Bank Reconciliation')
        }
      } finally {
        this.isSaving = false
      }
    },
    /**
     * Validates form data and submits if pass, show err if fail
     */
    async create () {
      let form = { ...this.form }
      let markAsComplete = false
      delete form.previous_balance
      delete form.show_all_checks
      delete form.cleared_amount
      delete form.difference
      delete form.cash_account_balance
      if (form.hasOwnProperty('completed_time')) {
        markAsComplete = !!form.completed_time
        delete form.completed_time
      }
      let validationErrors = validate(form, this.fields)
      if (form.end < form.start && !validationErrors) {
        validationErrors = {messages: []}
        validationErrors.messages.push('Statement End Date cannot be before the Statement Start Date')
      }
      if (validationErrors.messages) {
        let offset = 0                                                                      // Set offset so that notifs don't overlap each other
        validationErrors.messages.map(v => {                                                // Render validation error notifs
          notify(this, 'error', v, offset)
          offset += 100
        })
        return { r: null, markAsComplete: false, err: 'validation' }
      }
      try {
        const r = await this.$store.dispatch('saveBankRec', form)
        return { r, markAsComplete, err: null }
      } catch (e) {
        console.log(e)
        return { r: null, markAsComplete: false, err: e.message }
      }
    },
    /**
     * Updates a bankrec
     * @return {Promise<any>}
     */
    async update () {
      let ret = {}
      let markAsComplete = false
      let form = { ...this.form }
      if (form.hasOwnProperty('completed_time')) {
        markAsComplete = !!form.completed_time
        delete form.completed_time
      }
      delete form.previous_balance
      delete form.show_all_checks
      delete form.cleared_amount
      delete form.difference
      delete form.cash_account_balance
      for (let field in form) {
        if (this.prevFormState[field] !== form[field]) {
          ret[field] = form[field]
        }
      }
      try {
        this.isSaving = true
        const updated = await this.$store.dispatch('updateBankRec', { id: this.bankRec.id, data: { ...ret } })
        let validationErrors = validate(form, this.fields)
        if (form.end < form.start && !validationErrors) {
          notify(this, 'error', 'Statement End Date cannot be before the Statement Start Date')
        } else {
          if (updated && Object.keys(updated).length) {
            let completed
            if (markAsComplete) {
              completed = await this.markAsComplete(this.bankRec.id)
            }
            notify(this, 'success', 'Bank Reconciliation saved')
            if (completed && Object.keys(completed).length) {
              this.bankRec = this.getBankRecById(updated.id)
              let promises = [
                this.$store.dispatch('fetchLinesWithQuery', { type: 'ap', query: { recon: updated.id } }),
                this.$store.dispatch('fetchLinesWithQuery', { type: 'je', query: { recon: updated.id } }),
                this.$store.dispatch('fetchLinesWithQuery', { type: 'pc', query: { recon: updated.id } }),
                this.$store.dispatch('fetchAllChecksWithQuery', { recon: updated.id }),
                this.$store.dispatch('fetchAllPaymentsWithQuery', { recon: updated.id })
              ]
              this.resetDataState()
              const apiResponse = await Promise.all(promises)
              if (apiResponse && apiResponse.length) {
                this.initializeData(apiResponse)
              } else {
                this.$router.push({name: this.altRoute ? this.altRoute : 'BankRecs'})
              }
            }
            return updated
          } else {
            throw new Error('Error saving bank rec')
          }
        }
      } catch (e) {
        console.log(e)
        return e
      } finally {
        this.isSaving = false
      }
    },
    /**
     * Updates reconcilables in batches
     * @param selection
     * @return {Promise<void>}
     */
    async batchUpdateReconcilables (selection) {
      let promises = selection.map(v => this.updateReconcilable(v, !!v.recon))
      try {
        this.busy = true
        await Promise.all(promises)
      } catch (e) {
        console.log(e)
      } finally {
        this.busy = false
      }
    },
    /**
     * Updates a line either by attaching it to or detaching it from this bankrec. If successful, the reconcilable is
     * updated with the response (i.e. it is given a recon: <id> property). If the request fails, the user is notified
     * and the reconcilable line/check is de-selected.
     * @param reconcilable   The line to update
     * @param detach         Whether this is a deletion
     * @return {Promise.<*[]>}
     */
    async updateReconcilable (reconcilable, detach = false) {
      const type = ['ap', 'je', 'pc'].includes(reconcilable.type) ? 'Line' : (reconcilable.type === 'payments' ? 'Payment' : 'Check')
      let payload = { id: reconcilable.id }
      let data = { recon: detach ? null : this.bankRec.id }
      if (type === 'Check' || type === 'Payment') {
        payload.data = data
      } else {
        payload.type = reconcilable.type
        payload.line = data
      }
      try {
        const resp = await this.$store.dispatch(`update${type}`, payload)
        if (resp && resp.id) {
          reconcilable.recon = resp.recon
          return resp
        } else {
          throw new Error(`Error ${detach ? 'removing' : 'applying'} line or check to this bank reconciliation`)
        }
      } catch (e) {
        console.log(e)
        notify(this, 'warning', `Error ${detach ? 'removing' : 'applying'} line or check to this bank reconciliation`)
        let tableContainer = this.$refs[`${reconcilable.type}Table`]
        if (tableContainer) {
          let table = tableContainer.$children[0]
          if (table) {
            table.toggleRowSelection(reconcilable, false)
          }
        }
        return e
      }
    },
    /**
     * Updates the lines and or checks associated with a bankrec
     * @param recon   Bankrec ID
     * @return {Promise.<*[]>}
     */
    async updateLineItems (recon) {
      let promises = []
      for (let s in this.selection) {
        if (this.selection[s].length) {
          this.selection[s].forEach(v => {
            let payload = { id: v.id }
            if (s === 'checks' || s === 'payments') {
              payload.data = { recon }
            } else {
              payload.type = s
              payload.line = { recon }
            }
            promises.push(this.$store.dispatch(`update${s === 'checks' ? 'Check' : (s === 'payments') ? 'Payment' : 'Line'}`, payload))
          })
        }
      }
      return await Promise.all(promises)
    },
    /**
     * Marks a bankrec as complete
     * @param id    Bankrec ID
     * @return {Promise<any>}
     */
    async markAsComplete (id) {
      return await this.$store.dispatch('updateBankRec', { id, data: { completed_time: moment().toISOString() } })
    },
    /**
     * Initializes form fields with default values
     */
    initializeFormFields () {
      const { getBankById, getDefaultBank } = this.$store.getters
      const bank = getBankById(this.$route.query.bank) || getDefaultBank
      this.form.bank = bank.id
      this.form.show_all_checks = true
      this.form.start = bank && this.statements && this.statements.next
        ? this.statements.next.start
        : moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD')
      this.form.end = bank && this.statements && this.statements.next
        ? this.statements.next.end
        : moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')
      this.form.completed_time = null
      this.form.previous_balance = this.statements.next.ending_balance
      this.calculateSelectedItems()
    },
    /**
     * Parses an ISO 8601 formatted timestamp to YYYY-MM-DD HH:mm format
     */
    parseTimestamp (timestamp) {
      return moment.parseZone(timestamp).format('YYYY-MM-DD')
    },
    /**
     * Calculates cleared_amount and difference based on selected items as well as ending balance
     * Special thanks to @baseprime
     */
    calculateSelectedItems () {
      let amount = 0
      const { balance_decimal, previous_balance } = this.form
      const balance = parseFloat(balance_decimal) ? parseFloat(balance_decimal) : parseFloat('0.00')
      let prevBalance = parseFloat(previous_balance) ? parseFloat(previous_balance) : parseFloat('0.00')
      let itemsValue = {}
      let selectedItems = []
      for (let type in this.selection) {
        selectedItems = [...selectedItems, ...this.selection[type]]
      }
      selectedItems.forEach(lineItem => {
        let rowAmount = parseFloat(lineItem.amount_decimal)
        if (lineItem.meta_type === 'papercheck' || lineItem.meta_type === 'payment') {   // Offset Checks, because their amounts are positive
          rowAmount = rowAmount * -1
        }
        amount = parseFloat(amount) + parseFloat(rowAmount)
        if (typeof itemsValue[lineItem.meta_type] === 'undefined') {
          itemsValue[lineItem.meta_type] = [lineItem.id]
        } else {
          itemsValue[lineItem.meta_type].push(lineItem.id)
        }
      })
      let diff = balance - (prevBalance + amount)
      this.form.cleared_amount = Number(amount).toFixed(2)
      this.form.difference = Number(diff).toFixed(2)
      this.saveable = parseFloat(this.form.difference) === 0 && !isNaN(parseFloat(balance_decimal))
    },
    /**
     * Handles paginating between reconcilable transaction pages
     * @param page  Target page
     */
    async changePage (page) {
      let promise = new Promise((resolve, reject) => {
        this.currentPage = page
        resolve()
      })
      try {
        await promise
        // if (this.restoreSelection) {
        //   this.restoreSelection()
        // }
        this.gridApi.paginationGoToPage(page - 1)
      } catch (e) {
        console.log(e)
      }
    },
    /**
     * Changes tabs when a different tab is clicked, and resets paginator to page 1
     * @param tab   Target tab
     */
    async changeTabs (tab) {
      let promise = new Promise((resolve, reject) => {
        this.tableReady = false
        this.currentPage = 1
        this.activeTab = tab
        if (tab) {
          setTimeout(() => {
            this.tableReady = true
            this.tabChanged = true
            resolve()
          })
        } else {
          reject('Error changing tabs')
        }
      })
      try {
        await promise
        if (typeof this.restoreSelection === 'function') {
          this.restoreSelection()
        }
      } catch (e) {
        console.log(e)
      }
    },
    /**
     * Returns the formatted entity name to use as tab label
     * @param type
     */
    getTabLabel (type) {
      switch (type) {
        case 'je': return 'Journal Entries'
        case 'journalentryline': return 'Journal Entry Line'
        case 'ap': return 'AP Invoices'
        case 'accountspayableline': return 'Accounts Payable Line'
        case 'pc': return 'Petty Cash'
        case 'pettycashline': return 'Petty Cash Line'
        case 'checks': return 'Checks'
        case 'papercheck': return 'Check'
        case 'payment': return 'EFT/WR/MC'
        case 'payments': return 'EFT/WR/MC'
      }
    },
    /**
     * Shows the ledger item if the user clicks on "View in Ledger" on a given check table entry
     * @param index
     * @param row
     */
    showLedgerItem (index, row) {
      const createRelatedTransactionsData = () => {
        if (row.meta_type === 'papercheck' || row.meta_type === 'payment') {
          return row.related_transactions.map(v => {
            return { type: 'AP', id: v, path: `/aps/${v}` }
          })
        }
        let path, type
        switch (row.meta_type) {
          case 'accountspayableline':
            type = 'AP'
            path = `/aps/${row.transaction_id}`
            break
          case 'journalentryline':
            type = 'JE'
            path = `/jes/${row.transaction_id}`
            break
          case 'pettycashline':
            type = 'PC'
            path = `/pettycash/${row.transaction_id}`
        }
        return [{ type, id: row.transaction_id, path }]
      }
      this.ledgerItem.meta_name = row.meta_name
      this.ledgerItem.type = this.getTabLabel(row.meta_type)
      this.ledgerItem.amount = row.amount_decimal
      this.ledgerItem.description = row.description
      this.ledgerItem.date = this.parseTimestamp(row.time_posted)
      this.ledgerItem.related = createRelatedTransactionsData()
      this.ledgerModalVisible = true
    },
    /**
     * Returns the appropriate label for the table's ID column, depending on whether the entities shown
     * are je, ap, checks, or all
     * @param type
     * @return {string}
     */
    getIdColLabel (type) {
      if (type === 'checks' || type === 'payments') {
        return 'Number'
      }
      return 'ID'
    },
    /**
     * Returns the appropriate prop value for each row in the table's ID column, depending on whether the
     * row is a je, ap, or check
     * @param entity
     * @return {string}
     */
    getIdColProp (entity) {
      if (entity.meta_type && entity.meta_type === 'papercheck') {
        return entity['number']
      }
      return entity['ledger_id']
    },
    /**
     * Initializes the form with existing data (if editing)
     */
    initializeForm () {
      if (this.bankRec && Object.keys(this.bankRec).length) {
        this.form.bank = this.bankRec.bank || ''
        this.form.start = this.bankRec.start || ''
        this.form.end = this.bankRec.end || ''
        this.form.balance_decimal = this.bankRec.balance_decimal || '0.00'
        this.form.previous_balance = this.statements && this.statements.next ? this.statements.next.ending_balance : '0.00'
        this.form.show_all_checks = true
        this.calculateSelectedItems()
        this.form.completed_time = this.bankRec.completed_time || false
      }
    },
    /**
     * Fetches and sets the cash account balance of the current bank, as a function of the sum of the bank's
     * cash and deposits accounts
     * @return {Promise<string>}
     */
    async getBankBalance () {
      const { bank } = this
      this.form.cash_account_balance = ''
      if (!(bank && Object.keys(bank).length)) {
        return ''
      }
      try {
        const balanceRequests = [
          this.$store.dispatch('fetchAccountBalanceById', { id: bank.default_account_cash, currency: bank.currency, includeItems: false }),
          this.$store.dispatch('fetchAccountBalanceById', { id: bank.default_account_deposits, currency: bank.currency, includeItems: false })
        ]
        const responses = await Promise.all(balanceRequests)
        if (responses && responses.length && responses.some(resp => resp.balance)) {
          let acctBalances = {}
          responses.filter(resp => resp.id && resp.balance).forEach(resp => {
            acctBalances[resp.id] = resp.balance
          })
          this.form.cash_account_balance = Object.values(acctBalances).reduce((a, b) => a + parseFloat(b), 0)
        }
      } catch (e) {
        console.log(e)
      }
    }
  },
  computed: {
    pageCount () {
      let ret = {}
      for (let r in this.reconcilables) {
        ret[r] = Math.floor(this.reconcilables[r].data.length / this.pageLimit)
      }
      return ret
    },
    /**
     * Returns the reconcilable lines or checks for the current page
     * @return {*}
     */
    paginatedData () {
      // const start = (this.currentPage - 1) * this.pageLimit
      // const end = start + this.pageLimit
      return this.reconcilables[this.activeTab].data
    },
    /**
     * Gets the bank rec fields returned from an OPTIONS call to /bankrec and returns the fields necessary
     * to render the form (with some formatting). See bankrec store module for more.
     * @return {Array}
     */
    fields () {
      let fields = this.$store.getters.getBankRecFields
      let newFields = []
      if (fields) {
        newFields[0] = { ...fields.find(v => v.name === 'bank') }
        newFields[0]['read_only'] = (this.bankRec && !!this.bankRec.id) || this.isSaving
        newFields[0]['hidden'] = this.bankRec && !!this.bankRec.id
        newFields[1] = { ...fields.find(v => v.name === 'start') }
        newFields[1]['read_only'] = this.isSaving
        newFields[2] = { ...fields.find(v => v.name === 'end') }
        newFields[2]['read_only'] = this.isSaving
        newFields[3] = { ...fields.find(v => v.name === 'balance_decimal') }
        newFields[3]['read_only'] = this.isSaving
        newFields[3]['fieldType'] = 'money'
        newFields[4] = {
          fieldType: 'checkbox',
          label: 'Show All Transactions',
          name: 'show_all_checks',
          read_only: !this.form.bank || this.isSaving
        }
        newFields[5] = {
          fieldType: 'money',
          label: 'Cash Account Balance',
          name: 'cash_account_balance',
          read_only: true
        }
        newFields[6] = {
          fieldType: 'money',
          label: 'Previous Balance',
          name: 'previous_balance',
          read_only: true
        }
        newFields[7] = {
          fieldType: 'money',
          label: 'Cleared Amount',
          name: 'cleared_amount',
          read_only: true
        }
        newFields[8] = {
          fieldType: 'money',
          label: 'Difference',
          name: 'difference',
          read_only: true
        }
        newFields[9] = { ...fields.find(v => v.name === 'completed_time') }
        newFields[9]['fieldType'] = 'checkbox'
        newFields[9]['read_only'] = !this.saveable || this.isSaving
        if (!(this.bankRec && this.bankRec.id) && this.$store.getters.getAllBankRecs && this.$store.getters.getAllBankRecs.length) {
          newFields[0].choices = newFields[0].choices.filter(({ value }) => !this.existingDrafts[value])
        }
      }
      return newFields
    },
    /**
     * Returns an object where each key is the id of a bank for which there exists a draft bank rec.
     * @return {{}}
     */
    existingDrafts () {
      const drafts = {}
      this.$store.getters.getAllBankRecs.forEach(recon => {
        if (recon.completed_time === null) {
          drafts[recon.bank] = true
        }
      })
      return drafts
    },
    /**
     * Returns the statements associated with the currently selected bank
     * @return {Object}
     */
    statements () {
      return this.$store.getters.getBankStatementsByBank(this.bank.id)
    },
    /**
     * Returns the form difference property
     */
    difference () {
      return this.form.difference
    },
    /**
     * Returns the currently selected bank model from the store
     * @return {*}
     */
    bank () {
      return this.$store.getters.getBankById(this.form.bank)
    },
    /**
     * Checks whether the form has been changed.
     * @returns {boolean}
     */
    formHasChanged () {
      let comparisonArray = []
      if (!Object.keys(this.prevFormState).length) return true
      for (let field in this.form) {
        if (field === 'balance_decimal') continue
        if (field === 'bank') {
          comparisonArray.push(this.form.bank !== this.prevFormState.bank)
        } else {
          comparisonArray.push(this.form[field] !== this.prevFormState[field])
        }
      }
      return comparisonArray.some(v => v === true)
    },
    /**
     * Returns the currency based on the selected bank
     */
    currency () {
      if (!(this.form && this.form.bank)) return null
      if (this.bank && this.bank.currency) {
        return this.$store.getters.getCurrencyById(this.bank.currency)
      }
      return null
    },
    /**
     * Calculates the reconciled amount
     */
    reconciledAmount () {
      const sel = Object.values(this.selection).reduce((a, b) => a.concat(b), [])
      let amt = sel.reduce((a, b) => {
        const amt = (b.type === 'checks' || b.type === 'payments') ? (parseFloat(b.amount_decimal) * -1) : parseFloat(b.amount_decimal)
        return parseFloat(a) + amt
      }, 0)
      if ((amt || amt === 0) && !isNaN(amt)) {
        amt = amt.toFixed(2)
      }
      return amt
    }
  },
  watch: {
    difference () {
      if (this.saveable && (!this.bankRec || !this.bankRec.completed_time)) {
        this.form.completed_time = null
        this.checkIfComplete()
      }
    },
    /**
     * Updates the cash_account_balance field if the bank changes
     */
    bank () {
      if (this.prevFormState.bank && this.prevFormState.bank !== this.form.bank) {
        this.getBankBalance()
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/BankRecs.js